import React from "react";
import { Chip, Skeleton } from "./UI";

import { mapCategoryItem } from "../utils/mappers";

export default function SectionsSidebar({ 
  sections, 
  activeSection, 
  sectionTotals, 
  loadingSectionTotals, 
  onSectionClick,
  isDarkMode,
  catMap = {},
  catSummaries = {}
}) {
  return (
    <aside className={`w-full lg:w-[380px] shrink-0 border-b lg:border-b-0 lg:border-r ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Sections
          </div>
          <Chip>{sections.length}</Chip>
        </div>
        <div className="space-y-2 max-h-[42vh] overflow-y-auto pr-1 lg:max-h-none lg:overflow-visible">
          {sections.length === 0 ? (
            <>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </>
          ) : (
            sections.map((sec) => {
              const total = sectionTotals?.[sec];
              const totalLabel = typeof total === "number" ? `${total} Q` : "—";

              // Progress bar logic
              let completionPercent = 0;
              const cats = (catMap?.[sec] || [])
                .map(mapCategoryItem)
                .map((x) => x.label);

              if (cats.length > 0) {
                let totalRight = 0;
                let totalWrong = 0;
                let totalUnanswered = 0;
                for (const cat of cats) {
                  const summary = catSummaries?.[cat];
                  if (summary?.stats) {
                    const stats = summary.stats;
                    if (stats.total) {
                      const right = stats.right || 0;
                      const wrong = stats.wrong || 0;
                      const unanswered = stats.un || 0;
                      totalRight += right;
                      totalWrong += wrong;
                      totalUnanswered += unanswered;
                    }
                  }
                }
                const totalQuestions = totalRight + totalWrong + totalUnanswered;
                completionPercent = totalQuestions > 0 ? Math.round((totalRight / totalQuestions) * 100) : 0;
              }

              return (
                <button
                  key={sec}
                  onClick={() => onSectionClick(sec)}
                  className={[
                    "w-full text-left px-3 py-2 rounded-2xl border transition",
                    "flex flex-col gap-2",
                    sec === activeSection
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                      : "bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-200",
                  ].join(" ")}
                  title={sec}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0 flex-1 truncate whitespace-nowrap">{sec}</span>
                    <span
                      className={[
                        "shrink-0 text-[11px] px-2 py-1 rounded-full border whitespace-nowrap",
                        sec === activeSection
                          ? "bg-white/20 border-white/30 text-white"
                          : "bg-slate-50 dark:bg-slate-600 border-slate-200 dark:border-slate-500 text-slate-700 dark:text-slate-300",
                      ].join(" ")}
                      title="Total questions in section"
                    >
                      {loadingSectionTotals && sec === activeSection && typeof total !== "number" ? "…" : totalLabel}
                    </span>
                    {sec === activeSection ? (
                      <span className="shrink-0 text-[11px] bg-white/20 px-2 py-1 rounded-full whitespace-nowrap">Active</span>
                    ) : null}
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-2 bg-green-500 transition-all"
                      style={{ width: `${completionPercent}%` }}
                    />
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-right">
                    {completionPercent}% Complete
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </aside>
  );
}
